// lib/slides/resizeFrame.ts
//
// The frame arithmetic behind the eight resize handles, kept out of the canvas
// component so it can be tested without React.
//
// Every geometry bug in this module has been invisible in code review and
// obvious on screen - a proposal stretched 71% vertically, a caption painted
// under the export chrome - so this is the kind of code that earns a test.
import type { SlideElementFrame } from '#types/types';

export const normFrame = (f: SlideElementFrame): SlideElementFrame => ({
  x: Math.min(f.x, f.x + f.w), y: Math.min(f.y, f.y + f.h), w: Math.abs(f.w), h: Math.abs(f.h),
});

/** The eight box handles, plus an arrow's two endpoints. */
export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'p0' | 'p1';

/** Apply a handle drag (in page points) to a frame. Box handles move the edges
 *  they belong to; arrow handles move an endpoint, keeping the vector signed.
 *
 *  With `uniform` (Shift held) EVERY handle keeps the element's proportions:
 *
 *  - A corner keeps the aspect ratio and anchors the opposite corner.
 *  - An EDGE keeps it too. The dragged axis leads and its opposite edge stays
 *    put; the free axis follows the ratio and grows about the frame's centre
 *    line, so the element stays where it looked like it was. Anchoring the free
 *    axis at one end instead would slide the element sideways on a drag that
 *    only ever moved in and out.
 *  - An ARROW endpoint keeps the arrow's DIRECTION and changes only its length -
 *    the endpoint is projected onto the original vector. That is what
 *    "proportional" means for something with no width or height, and it is what
 *    Shift does to a line in every drawing tool.
 *
 *  Exported for the tests: it is pure geometry, and unverified frame arithmetic
 *  is what stretched a proposal 71% vertically earlier in this module's life. */
export const resizeFrameBy = (
  frame: SlideElementFrame,
  handle: ResizeHandle,
  dx: number,
  dy: number,
  uniform = false,
  rotation = 0,
): SlideElementFrame => {
  if (rotation) {
    const angle = rotation * Math.PI / 180;
    const cos = Math.cos(angle), sin = Math.sin(angle);
    const next = resizeFrameBy(frame, handle, cos * dx + sin * dy, -sin * dx + cos * dy, uniform);
    const shiftX = next.x + next.w / 2 - frame.x - frame.w / 2;
    const shiftY = next.y + next.h / 2 - frame.y - frame.h / 2;
    return { ...next,
      x: frame.x + frame.w / 2 + cos * shiftX - sin * shiftY - next.w / 2,
      y: frame.y + frame.h / 2 + sin * shiftX + cos * shiftY - next.h / 2,
    };
  }
  if (handle === 'p0' || handle === 'p1') {
    const length = Math.hypot(frame.w, frame.h);
    if (uniform && length > 0) {
      // Unit vector along the arrow. The dragged end is projected onto it, so
      // the pointer sets the LENGTH (including a negative one, which flips the
      // arrow through its fixed end) and never the angle.
      const ux = frame.w / length;
      const uy = frame.h / length;
      // p1 moves the head, p0 the tail; the vector shrinks by a tail drag.
      const vx = handle === 'p1' ? frame.w + dx : frame.w - dx;
      const vy = handle === 'p1' ? frame.h + dy : frame.h - dy;
      const w = ux * (vx * ux + vy * uy);
      const h = uy * (vx * ux + vy * uy);
      return handle === 'p1'
        ? { ...frame, w, h }
        : // The head is x+w, y+h and must not move, so the tail absorbs it all.
          { x: frame.x + (frame.w - w), y: frame.y + (frame.h - h), w, h };
    }
    return handle === 'p1'
      ? { ...frame, w: frame.w + dx, h: frame.h + dy }
      : { x: frame.x + dx, y: frame.y + dy, w: frame.w - dx, h: frame.h - dy };
  }

  if (uniform && frame.w > 0 && frame.h > 0) {
    const ratio = frame.w / frame.h;
    const east = handle.includes('e');
    const west = handle.includes('w');
    const south = handle.includes('s');
    const north = handle.includes('n');
    // Deltas expressed as GROWTH, so a west/north drag reads the same as an
    // east/south one and the maths below does not need to know which handle.
    let dw = east ? dx : west ? -dx : 0;
    let dh = south ? dy : north ? -dy : 0;
    if ((east || west) && (north || south)) {
      // Corner: follow whichever axis the pointer moved further, measured in the
      // same units. Comparing dw against dh directly would make a wide frame
      // follow its height on almost every drag.
      if (Math.abs(dw) >= Math.abs(dh * ratio)) dh = dw / ratio;
      else dw = dh * ratio;
    } else if (east || west) dh = dw / ratio;
    else dw = dh * ratio;
    const w = frame.w + dw;
    const h = frame.h + dh;
    return {
      // On the dragged axis the opposite edge is the anchor. On the free axis
      // there is no opposite edge to anchor to, so the centre holds.
      x: east ? frame.x : frame.x + (frame.w - w) / (west ? 1 : 2),
      y: south ? frame.y : frame.y + (frame.h - h) / (north ? 1 : 2),
      w,
      h,
    };
  }

  const next = { ...frame };
  if (handle.includes('w')) {
    next.x = frame.x + dx;
    next.w = frame.w - dx;
  }
  if (handle.includes('e')) next.w = frame.w + dx;
  if (handle.includes('n')) {
    next.y = frame.y + dy;
    next.h = frame.h - dy;
  }
  if (handle.includes('s')) next.h = frame.h + dy;
  return next;
};
